import { type SkillPick } from "encamp-shared/src/skills/skillPickContext";
import { useCurrentUser } from "hooks/useCurrentUser";
import { useLocalStorageState } from "hooks/useLocalStorageState";
import { useTenant } from "hooks/useTenant";
import { useCallback, useMemo, useRef, useState } from "react";

import { skillPicksInContent, slashQueryAtCaret } from "./selectedSkillContext";
import {
  buildSkillMenuSections,
  pushRecentSkill,
  slashMenuKeyAction,
  type ScoutSkillMenuItem,
} from "./skillMenuModel";
import { useScoutSkillCatalog } from "./useScoutSkillCatalog";

/** The parts of a keydown the slash menu reads. */
export type SlashKeyEvent = Pick<React.KeyboardEvent, "key" | "shiftKey" | "preventDefault"> & {
  nativeEvent: Pick<KeyboardEvent, "isComposing">;
};

/**
 * Swap the `/query` at the caret for `/slug ` (left in the message; the
 * highlight is display only) and tell React. The new value, or null if no query.
 */
function replaceSlashQueryWithSlug(textarea: HTMLTextAreaElement, slug: string): string | null {
  const slash = slashQueryAtCaret(textarea);
  if (!slash) {
    return null;
  }
  const insertion = `/${slug} `;
  const next =
    textarea.value.slice(0, slash.start) +
    insertion +
    textarea.value.slice(textarea.selectionStart);
  textarea.value = next;
  const caret = slash.start + insertion.length;
  textarea.setSelectionRange(caret, caret);
  textarea.dispatchEvent(new Event("input", { bubbles: true }));
  return next;
}

/** Arrow keys, Enter/Tab and Escape while the menu is open; true when the key was used. */
function useSlashKeyDown(params: {
  menuOpen: boolean;
  /** Re-reads the caret; false when the `/token` is gone. */
  syncSlashFromInput: () => boolean;
  filteredItems: ScoutSkillMenuItem[];
  highlightedIndex: number;
  setHighlightedIndex: (index: number) => void;
  selectSkill: (item: ScoutSkillMenuItem) => void;
  closeMenu: () => void;
}) {
  const { menuOpen, syncSlashFromInput, filteredItems, highlightedIndex } = params;
  const { setHighlightedIndex, selectSkill, closeMenu } = params;
  return useCallback(
    (event: SlashKeyEvent): boolean => {
      // Enter that ends an IME composition belongs to the IME.
      if (!menuOpen || event.nativeEvent.isComposing) {
        return false;
      }
      // Never act on (or swallow keys for) a menu whose token is gone.
      if (!syncSlashFromInput()) {
        return false;
      }

      const action = slashMenuKeyAction({
        key: event.key,
        shiftKey: event.shiftKey,
        itemCount: filteredItems.length,
        highlightedIndex,
      });
      if (!action) {
        return false;
      }
      event.preventDefault();
      if (action.type === "highlight") {
        setHighlightedIndex(action.index);
      } else if (action.type === "close") {
        closeMenu();
      } else {
        const item = filteredItems[action.index];
        if (item) {
          selectSkill(item);
        }
      }
      return true;
    },
    [
      closeMenu,
      filteredItems,
      highlightedIndex,
      menuOpen,
      selectSkill,
      setHighlightedIndex,
      syncSlashFromInput,
    ]
  );
}

type UseScoutSkillSlashArgs = {
  inputRef: React.RefObject<HTMLTextAreaElement | null>;
  setHasContent: (hasContent: boolean) => void;
  /** Fired after slash-select mutates the textarea (highlight mirror sync). */
  onValueChange?: (value: string) => void;
  disabled?: boolean;
};

export function useScoutSkillSlash({
  inputRef,
  setHasContent,
  onValueChange,
  disabled = false,
}: UseScoutSkillSlashArgs) {
  const { tenantId } = useTenant();
  const { user } = useCurrentUser();
  const { enabled, items: allItems } = useScoutSkillCatalog({
    skip: disabled,
    fetchPolicy: "cache-and-network",
  });

  // `query` is the `/query` at the caret, or null while the menu is closed.
  const [slash, setSlash] = useState<{ query: string | null; highlightedIndex: number }>({
    query: null,
    highlightedIndex: 0,
  });
  const { query: slashQuery, highlightedIndex } = slash;
  const menuOpen = enabled && slashQuery !== null;
  const setHighlightedIndex = useCallback((index: number) => {
    setSlash((current) => ({ ...current, highlightedIndex: index }));
  }, []);

  // Per user and tenant, newest first. Browser-local until recents live server-side.
  const [recentIds, setRecentIds] = useLocalStorageState<string[]>(
    `scout-skill-recent-ids:${tenantId ?? "none"}:${user?.id ?? "anon"}`,
    { defaultValue: [] }
  );

  // Slug -> id of the skill last picked from the menu for it, until the message is sent.
  const picksRef = useRef(new Map<string, string>());

  const knownSlugs = useMemo(() => new Set(allItems.map((item) => item.slug)), [allItems]);

  const sections = useMemo(
    () => buildSkillMenuSections({ items: allItems, query: slashQuery ?? "", recentIds }),
    [allItems, recentIds, slashQuery]
  );
  const filteredItems = sections.flat;

  /**
   * Re-read the `/query` at the caret. Runs on every change and caret move
   * (click, arrows, Home/End), so the menu never outlives its token. Returns
   * whether the menu is open.
   */
  const syncSlashFromInput = useCallback((): boolean => {
    const textarea = inputRef.current;
    const query = enabled && textarea ? (slashQueryAtCaret(textarea)?.query ?? null) : null;
    // A new query starts the highlight over; a caret move within the same one keeps it.
    setSlash((current) => (current.query === query ? current : { query, highlightedIndex: 0 }));
    return query !== null;
  }, [enabled, inputRef]);

  const closeMenu = useCallback(() => {
    setSlash({ query: null, highlightedIndex: 0 });
  }, []);

  const selectSkill = useCallback(
    (item: ScoutSkillMenuItem) => {
      const textarea = inputRef.current;
      if (textarea) {
        const next = replaceSlashQueryWithSlug(textarea, item.slug);
        if (next !== null) {
          setHasContent(next.trim().length > 0);
          onValueChange?.(next);
        }
        textarea.focus();
      }
      picksRef.current.set(item.slug, item.id);
      setRecentIds((prev) => pushRecentSkill(prev, item.id));
      closeMenu();
    },
    [closeMenu, inputRef, onValueChange, setHasContent, setRecentIds]
  );

  /**
   * The menu picks for slugs still in the message being sent, then forgets
   * them all. A typed `/slug` has no pick; Scout resolves it by slug.
   */
  const takeSkillPicks = useCallback((content: string): SkillPick[] => {
    const taken = skillPicksInContent(picksRef.current, content);
    picksRef.current.clear();
    return taken;
  }, []);

  const handleSlashKeyDown = useSlashKeyDown({
    menuOpen,
    syncSlashFromInput,
    filteredItems,
    highlightedIndex,
    setHighlightedIndex,
    selectSkill,
    closeMenu,
  });

  return {
    enabled,
    menuOpen,
    filteredItems,
    sections,
    knownSlugs,
    slashQuery: slashQuery ?? "",
    highlightedIndex,
    setHighlightedIndex,
    selectSkill,
    takeSkillPicks,
    syncSlashFromInput,
    handleSlashKeyDown,
    closeMenu,
  };
}
